import type { TasApplicationExtension as TasApplication } from 'tio-data/data/resources/tas-application.ext';

const REIMBURSEMENT = 'TAS.AssistanceManner.REIMBURSEMENT';
const UNSETTLED = 'TAS.AssistanceState.UNSETTLED';
const PAYABLE = 'TAS.AssistanceState.PAYABLE';
const UNCOUNTED_STATES = new Set(['TAS.AssistanceState.CREATED', 'TAS.AssistanceState.ABANDONED']);
const DECLINED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.ABANDONED',
]);

function countedReimbursements(application: Pick<TasApplication, 'tasAssistances'>) {
  return (application.tasAssistances ?? []).filter(
    (assistance) => assistance.manner === REIMBURSEMENT && !UNCOUNTED_STATES.has(assistance.state)
  );
}

/**
 * The application's current reimbursement amount, read from its REIMBURSEMENT assistances rather
 * than FINAL_APPROVED_TOTAL. An assistance amount edit (a held-funds reduction or a drawer edit)
 * does not rewrite that field, and once payments exist the API itself reads these rows as the
 * approved total, so an editable approval must start from them.
 *
 * @param application - needs `tasAssistances` loaded for the result to be meaningful
 * @returns cents, signed (repayments subtract; CREATED and ABANDONED rows are skipped), or
 *   undefined when the application has no reimbursement assistance yet — callers then fall back
 *   to the application's approved-total field
 */
export function currentReimbursementTotal(
  application: Pick<TasApplication, 'tasAssistances'>
): number | undefined {
  const reimbursements = countedReimbursements(application);
  if (!reimbursements.length) return undefined;

  return reimbursements.reduce((total, assistance) => total + assistance.amount, 0);
}

/**
 * The amount to show as approved: {@link currentReimbursementTotal}, except on a declined
 * application that still has a PAYABLE reimbursement queued. Declining does not cancel a payment
 * made before the decision, so there the approved-total field is the decision. When the decline
 * went through update_approved_total (an audit denial), the rows were already reconciled and the
 * field was left stale, so the rows are shown.
 *
 * @returns cents, or undefined when the approved-total field should be shown
 */
export function approvedReimbursementAmount(
  application: Pick<TasApplication, 'tasAssistances' | 'state'>
): number | undefined {
  const hasQueuedPayment = countedReimbursements(application).some(
    (assistance) => assistance.state === PAYABLE
  );
  if (DECLINED_APPLICATION_STATES.has(application.state) && hasQueuedPayment) return undefined;
  return currentReimbursementTotal(application);
}

/**
 * The reimbursement still held but not yet on the ledger: the summed UNSETTLED rows, while every
 * reimbursement is UNSETTLED. Before final approval this, not PRE_APPROVED_TOTAL, is what an
 * approver's reduction changed.
 *
 * @returns cents, or undefined when there are no rows or any reimbursement is past UNSETTLED
 */
export function unsettledReimbursementHold(
  application: Pick<TasApplication, 'tasAssistances'>
): number | undefined {
  const reimbursements = countedReimbursements(application);
  if (!reimbursements.length) return undefined;
  if (reimbursements.some((assistance) => assistance.state !== UNSETTLED)) return undefined;

  return reimbursements.reduce((total, assistance) => total + assistance.amount, 0);
}
